import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, Search, ArrowLeft, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogContentWithoutClose,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Pagination } from '../../../components/ui/Pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { pipefitterTriggerReportComplimentary } from '../../../redux/Pipefitter/actions';
import {
  fetchPipeDataList,
  mapPipeDataListRowsToPickerItems,
  type PipeDataListPickerItem,
} from '../api/pipeDataList';
import { PipeDataReportGenerateModal } from './PipeDataReportGenerateModal';
import { SendReportEmailModal } from './SendReportEmailModal';
import type { DashboardProject } from '../../project/types/project';
import type { PipeStatusItem } from '../types';
import { PIPEFITTER_FULLSCREEN_DIALOG_CLASS, PIPEFITTER_STACKED_DIALOG_OVERLAY_CLASS } from '../constants/uiConstants';
import {
  buildCompatibilityLargeSelectionDescription,
  isLargeCompatibilitySelection,
} from '../helpers/reportGenerateThresholds';

interface PipeCompatibilityReportGenerateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  selectedPipe?: { pipeId: string; displayName?: string; pipeName?: string } | null;
  initialSelectedIds?: string[];
  onReportGenerated?: (pipeStatusIds?: string[]) => void;
  /** Full-screen shell when opened above another pipefitter dialog (e.g. report history detail). */
  dialogClassName?: string;
}

const DEFAULT_PIPE_PAGE_SIZE = 25;
const PIPE_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function clampPipePickerPageSize(n: number): number {
  return Math.min(200, Math.max(1, Math.floor(n)));
}

export const PipeCompatibilityReportGenerateModal: React.FC<PipeCompatibilityReportGenerateModalProps> = ({
  open,
  onOpenChange,
  project,
  selectedPipe,
  initialSelectedIds = [],
  onReportGenerated,
  dialogClassName,
}) => {
  const dispatch = useDispatch();
  const authUserEmail = useSelector(
    (state: { auth?: { user?: { email?: string } } }) => state.auth?.user?.email?.trim() ?? ''
  );
  const DEMO_PROJECT_NUMBER = process.env.REACT_APP_PIPEFITTER_DEMO_PROJECT_NUMBER ?? '';
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName ?? '';

  const [pipeDataListRows, setPipeDataListRows] = useState<PipeStatusItem[]>([]);
  const [isPipeListLoading, setIsPipeListLoading] = useState(false);
  const [pipeSearch, setPipeSearch] = useState('');
  const [selectedPipeIds, setSelectedPipeIds] = useState<Set<string>>(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateEmailModal, setShowGenerateEmailModal] = useState(false);
  const [pipeDataModalOpen, setPipeDataModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PIPE_PAGE_SIZE);
  const itemsPerPageRef = useRef(itemsPerPage);
  itemsPerPageRef.current = itemsPerPage;

  const pipeList: PipeDataListPickerItem[] = useMemo(
    () => mapPipeDataListRowsToPickerItems(pipeDataListRows),
    [pipeDataListRows]
  );

  const initialSelectedIdsKey = useMemo(
    () =>
      [...initialSelectedIds]
        .map(String)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .join(','),
    [initialSelectedIds]
  );

  useEffect(() => {
    if (!open) return;
    setPipeSearch('');
    setCurrentPage(1);
    setSelectedPipeIds(new Set(initialSelectedIdsKey ? initialSelectedIdsKey.split(',') : []));
  }, [open, initialSelectedIdsKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pipeSearch]);

  useEffect(() => {
    if (!open) {
      setPipeDataListRows([]);
      setIsPipeListLoading(false);
      return;
    }

    const pipeId = selectedPipe?.pipeId;
    const effectiveProjectNumber = projectNumber || DEMO_PROJECT_NUMBER;
    if (!effectiveProjectNumber || !pipeId) return;

    let cancelled = false;
    setIsPipeListLoading(true);
    fetchPipeDataList({
      project_id: projectId,
      project_num: effectiveProjectNumber,
      project_name: projectName,
      pipe_id: pipeId,
    })
      .then((rows) => {
        if (!cancelled) {
          setPipeDataListRows(rows);
          setIsPipeListLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPipeDataListRows([]);
          setIsPipeListLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectNumber, projectId, projectName, selectedPipe?.pipeId, DEMO_PROJECT_NUMBER]);

  useEffect(() => {
    if (!open) {
      setIsGenerating(false);
      setShowGenerateEmailModal(false);
      setPipeDataModalOpen(false);
      setCurrentPage(1);
      setItemsPerPage(DEFAULT_PIPE_PAGE_SIZE);
    }
  }, [open]);

  const searchFilteredPipes = useMemo(() => {
    if (!pipeSearch.trim()) return pipeList;
    const q = pipeSearch.trim().toLowerCase();
    return pipeList.filter((p) => p.pipeID.toLowerCase().includes(q));
  }, [pipeList, pipeSearch]);

  const totalFilteredPipes = searchFilteredPipes.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredPipes / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedPipes = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return searchFilteredPipes.slice(start, start + itemsPerPage);
  }, [searchFilteredPipes, currentPage, itemsPerPage]);

  const visiblePipes = paginatedPipes;

  const selectedCount = selectedPipeIds.size;
  const hasVisiblePipes = visiblePipes.length > 0;
  const isBusy = isGenerating;
  const isLargeSelection = isLargeCompatibilitySelection(selectedCount);
  const canUseLoginEmail = Boolean(authUserEmail);

  const isAllFilteredSelected =
    searchFilteredPipes.length > 0 &&
    searchFilteredPipes.every((p) => selectedPipeIds.has(p.id));
  const isSomeFilteredSelected =
    searchFilteredPipes.some((p) => selectedPipeIds.has(p.id)) && !isAllFilteredSelected;

  const pipeIdsForApi = useMemo(() => {
    if (selectedCount === 0) return [];
    return Array.from(selectedPipeIds).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [selectedCount, selectedPipeIds]);

  const handleTogglePipe = (id: string, checked: boolean) => {
    setSelectedPipeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSelectAllFiltered = (checked: boolean) => {
    if (checked) {
      setSelectedPipeIds((prev) => {
        const next = new Set(prev);
        searchFilteredPipes.forEach((p) => next.add(p.id));
        return next;
      });
    } else {
      setSelectedPipeIds((prev) => {
        const next = new Set(prev);
        searchFilteredPipes.forEach((p) => next.delete(p.id));
        return next;
      });
    }
  };

  const generateEmailDescription =
    isLargeSelection && authUserEmail
      ? buildCompatibilityLargeSelectionDescription(selectedCount, authUserEmail)
      : 'Enter the email address(es) to notify when the report is ready. Separate multiple addresses with commas.';

  const handleConfirmGenerateReport = (emails: string[]) => {
    if (!projectNumber || isBusy || pipeIdsForApi.length === 0) return;
    if (isLargeSelection && emails.length === 0) return;

    setIsGenerating(true);
    dispatch(
      pipefitterTriggerReportComplimentary(
        {
          projectNumber,
          pipeIds: pipeIdsForApi,
          pipeId: selectedPipe?.pipeId,
          ...(emails.length > 0
            ? { notifyEmail: emails[0], notifyEmails: emails }
            : {}),
        },
        () => {
          setIsGenerating(false);
          setShowGenerateEmailModal(false);
          onReportGenerated?.(pipeIdsForApi);
          onOpenChange(false);
        },
        () => setIsGenerating(false)
      )
    );
  };

  const handleOpenGenerateEmailModal = () => {
    if (!projectNumber || isBusy || pipeIdsForApi.length === 0) return;

    if (!isLargeSelection) {
      handleConfirmGenerateReport(authUserEmail ? [authUserEmail] : []);
      return;
    }

    if (!canUseLoginEmail) return;
    setShowGenerateEmailModal(true);
  };

  const handleOpenPipeDataReport = () => {
    if (selectionRequired || !projectNumber) return;
    setPipeDataModalOpen(true);
  };

  const pipeSizeLabel =
    selectedPipe?.displayName ?? selectedPipe?.pipeName ?? selectedPipe?.pipeId ?? '';

  const headerInfoItems = useMemo(() => {
    const items: { label: string; value: string; bgColor: string }[] = [];
    if (projectName) {
      items.push({ label: 'Project Name', value: projectName, bgColor: 'bg-indigo-100 text-indigo-800' });
    }
    if (projectNumber) {
      items.push({ label: 'Project Number', value: projectNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
    }
    if (pipeSizeLabel) {
      items.push({
        label: 'Pipe size',
        value: pipeSizeLabel,
        bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
      });
    }
    return items;
  }, [projectName, projectNumber, pipeSizeLabel]);

  const selectionRequired = selectedCount === 0;

  const dialogShellClass = dialogClassName ?? PIPEFITTER_FULLSCREEN_DIALOG_CLASS;
  const DialogShell = dialogClassName ? DialogContentWithoutClose : DialogContent;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell
        className={dialogShellClass}
        overlayClassName={dialogClassName ? PIPEFITTER_STACKED_DIALOG_OVERLAY_CLASS : undefined}
      >
        <div className="flex h-screen flex-col overflow-hidden">
        <DialogHeader className="shrink-0 border-b border-[#E2E5EA] bg-white px-4 pb-3 pt-4 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                className="shrink-0 h-9 w-9 p-0 border-[#E2E5EA] hover:bg-page-bg"
                variant="outline"
                size="icon"
                disabled={isBusy}
                aria-label="Back to reports list"
              >
                <ArrowLeft className="h-4 w-4 text-text-grey" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                  Generate Pipe Compatibility Report
                </DialogTitle>
                {headerInfoItems.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {headerInfoItems.map((item, idx) => (
                      <span
                        key={`${item.label}-${idx}`}
                        className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}
                      >
                        {item.label}: {item.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleOpenPipeDataReport}
                disabled={isBusy || selectionRequired || !projectNumber}
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Pipe Data Report
              </Button> */}
              <Button
                type="button"
                size="sm"
                onClick={handleOpenGenerateEmailModal}
                loading={isGenerating}
                loadingText="Generating…"
                disabled={
                  isBusy ||
                  selectionRequired ||
                  !projectNumber ||
                  (isLargeSelection && !canUseLoginEmail)
                }
                title={
                  isLargeSelection && !canUseLoginEmail
                    ? 'Sign in with an email address to generate large compatibility reports'
                    : undefined
                }
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Generate Report
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 pt-3 sm:px-6">
          <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-heritage-grey" />
              <Input
                placeholder="Search pipes"
                value={pipeSearch}
                onChange={(e) => setPipeSearch(e.target.value)}
                className="pl-9 h-9 text-[13px] bg-white border-[#E2E5EA]"
              />
            </div>
            <p className="text-[13px] text-heritage-grey">
              {selectedCount > 0 ? (
                <>
                  <span className="font-semibold text-blueprint-blue">{selectedCount}</span> pipe
                  {selectedCount !== 1 ? 's' : ''} selected
                </>
              ) : (
                'Select one or more pipes to generate a report or view pipe data report'
              )}
            </p>
          </div>

          {/* {isLargeSelection ? (
            <p className="mb-3 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {canUseLoginEmail
                ? `Large selection (${selectedCount} pipes): compatibility report generation may take longer. We will notify you at ${authUserEmail} when it is ready.`
                : 'Large selection: sign in with an email address to generate this report.'}
            </p>
          ) : null} */}

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-[#E2E5EA] bg-white shadow-sm">
            {isPipeListLoading && pipeList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-heritage-grey">
                <Loader2 className="h-6 w-6 animate-spin text-hero-red" />
                <p className="text-[13px]">Loading pipes…</p>
              </div>
            ) : !hasVisiblePipes ? (
              <div className="p-8 text-center text-[13px] text-heritage-grey">
                {pipeList.length === 0 ? 'No pipes loaded.' : 'No pipes match your search.'}
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-page-bg">
                    <TableRow className="border-b border-[#E2E5EA] hover:bg-transparent">
                      <TableHead className="w-12 text-center">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllFilteredSelected}
                            ref={(el) => {
                              if (el) (el as any).indeterminate = isSomeFilteredSelected;
                            }}
                            onCheckedChange={(c) => handleSelectAllFiltered(c === true)}
                            className="cursor-pointer"
                            aria-label={isAllFilteredSelected ? 'Deselect all pipes' : 'Select all pipes'}
                          />
                        </div>
                      </TableHead>
                      <TableHead className="w-20 text-center text-[12px] font-medium text-heritage-grey">
                        SL No
                      </TableHead>
                      <TableHead className="text-[12px] font-medium text-heritage-grey">Pipe ID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visiblePipes.map((pipe, index) => {
                      const isSelected = selectedPipeIds.has(pipe.id);
                      const slNo = (currentPage - 1) * itemsPerPage + index + 1;
                      return (
                        <TableRow key={pipe.id} className="border-b border-[#E2E5EA] hover:bg-page-bg/80">
                          <TableCell className="w-12 text-center">
                            <div className="flex items-center justify-center">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(c) => handleTogglePipe(pipe.id, c === true)}
                                className="cursor-pointer"
                                aria-label={`${isSelected ? 'Deselect' : 'Select'} ${pipe.pipeID}`}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center text-[13px] text-text-grey tabular-nums">
                            {slNo}
                          </TableCell>
                          <TableCell className="text-[13px] font-medium text-blueprint-blue whitespace-nowrap">
                            {pipe.pipeID}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
                {totalFilteredPipes > 0 ? (
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalFilteredPipes}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={(n) => {
                      const lim = clampPipePickerPageSize(n);
                      itemsPerPageRef.current = lim;
                      setItemsPerPage(lim);
                      setCurrentPage(1);
                    }}
                    pageSizeOptions={PIPE_PAGE_SIZE_OPTIONS}
                    showPageNumbers
                    maxVisiblePages={5}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
        </div>
      </DialogShell>
    </Dialog>

    <PipeDataReportGenerateModal
      open={pipeDataModalOpen}
      onOpenChange={setPipeDataModalOpen}
      project={project}
      selectedPipe={selectedPipe}
      initialSelectedIds={pipeIdsForApi}
      onReportGenerated={onReportGenerated}
    />

    <SendReportEmailModal
      open={showGenerateEmailModal && isLargeSelection}
      onOpenChange={(next) => {
        if (!isGenerating) setShowGenerateEmailModal(next);
      }}
      projectNumber={projectNumber}
      title="Generate Pipe Compatibility Report"
      description={generateEmailDescription}
      initialEmail={authUserEmail}
      emailReadOnly={isLargeSelection}
      isSending={isGenerating}
      submitLabel="Generate Report"
      submittingLabel="Starting…"
      onSend={handleConfirmGenerateReport}
    />
    </>
  );
};
